import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { Input } from './Input'
import { cx } from './cx'
import s from './SearchField.module.css'

/**
 * A VALUE SLOT WITH A GLYPH ON ITS LEADING EDGE.
 *
 * ── why this exists, and why it is not a prop on `Input` ───────────────────
 * `Input` is a bare `<input>` carrying the recess vocabulary. A leading glyph
 * needs a positioned wrapper, and adding one inside `Input` would change the
 * DOM shape under every caller in the estate — a form field would silently
 * gain an element, and the selectors, layouts and tests that reach for it
 * would all be reaching one level too shallow. So the glyph gets a component
 * that COMPOSES `Input` rather than a flag that reshapes it.
 *
 * ── what it replaces ──────────────────────────────────────────────────────
 * Three hand-rolled copies, in two hosts, and both hosts had written down that
 * they were stuck: `RelationshipWorkspace.module.css` says in as many words
 * that "`Input.module.css` has no leading-icon primitive, so there is nothing
 * in the kit to adopt here", and `RecentPinnedPanel` re-derived the same
 * absolute-position-plus-padding arithmetic independently. When two surfaces
 * write the same note about the same absence, the absence is the defect.
 *
 * ── the arithmetic, which is the whole reason a copy drifts ────────────────
 * The text has to clear the glyph: two spacing rungs plus the glyph's own
 * width. It is expressed in TOKENS, not in a literal, because a literal here
 * is a number no theme can move — which is the debt
 * `conformance/hostStyleAdoption.test.ts` counts. Both copies had it right on
 * the day they were written; neither could have stayed right through a change
 * to `--sp-2`.
 */
export interface SearchFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /** The glyph on the leading edge. Decorative: it is hidden from the reader,
   *  because the field's own accessible name is what names it. */
  icon?: ReactNode
  /** Applied to the WRAPPER, for a host that owns the field's placement. The
   *  `className` goes to the input itself, as it does on `Input`. */
  wrapClassName?: string
}

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  function SearchField({ icon, className, wrapClassName, ...rest }, ref) {
    return (
      <span className={cx(s.wrap, wrapClassName)}>
        {icon != null && <span className={s.icon} aria-hidden="true">{icon}</span>}
        <Input ref={ref} className={cx(icon != null && s.padded, className)} {...rest} />
      </span>
    )
  },
)
